import { Box, Skeleton, Stack, useTheme } from '@mui/material';
import React, { FunctionComponent } from 'react';

// Generic page skeleton — rendered by the router's defaultPendingComponent
// while a slow route resolves. Mirrors the TabsPage template layout:
// breadcrumb row, tab row with bottom divider, then a content area. Most
// pages share this shell, so the skeleton lines up with the real layout
// and the swap-in feels like a fill rather than a layout shift.
const PageSkeletonBase: FunctionComponent = () => {
  const theme = useTheme();

  return (
    <Stack
      direction="column"
      sx={{ height: '100%', width: '100%', position: 'relative' }}
    >
      {/* Breadcrumb row — matches BreadcrumbRoute padding in TabsPage. */}
      <Box sx={{ pl: theme.distances.medium, pt: theme.distances.small }}>
        <Skeleton variant="text" width={180} height={20} />
      </Box>

      {/* Tab row — same horizontal layout, padding, and bottom divider as
          the real Tabs row. Three tab placeholders + a pageActions slot. */}
      <Stack
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        sx={{
          pl: theme.distances.medium,
          pr: theme.distances.medium,
          pt: theme.distances.small,
          pb: theme.distances.small,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" spacing={4}>
          <Skeleton variant="text" width={80} height={28} />
          <Skeleton variant="text" width={80} height={28} />
          <Skeleton variant="text" width={80} height={28} />
        </Stack>
        <Skeleton variant="rectangular" width={120} height={32} />
      </Stack>

      {/* Content area — flex-grows to fill the remaining height, mirroring
          the active tabpanel. A header strip + a large body block keeps it
          neutral enough to fit any page that lands here. */}
      <Stack
        direction="column"
        spacing={theme.distances.small}
        sx={{
          flexGrow: 1,
          p: theme.distances.medium,
        }}
      >
        <Box display="flex" gap={theme.distances.small}>
          <Skeleton variant="rectangular" width="100%" height={96} />
          <Skeleton variant="rectangular" width="100%" height={96} />
          <Skeleton variant="rectangular" width="100%" height={96} />
        </Box>
        <Skeleton
          variant="rectangular"
          width="100%"
          sx={{ flexGrow: 1, minHeight: 240 }}
        />
      </Stack>
    </Stack>
  );
};

const PageSkeleton = React.memo(PageSkeletonBase);

export { PageSkeleton };
